import React, { useCallback, useEffect, useRef } from "react";

interface Props {
  children: React.ReactNode;
  id: "privacy" | "conditions" | "accessibility" | "cookies";
}

const SCROLL_GAP = 16;

const getScrollOffset = (container: HTMLElement | null) => {
  const toc = container?.querySelector(".toc");

  if (!(toc instanceof HTMLElement)) {
    return SCROLL_GAP;
  }

  const stickyTop = parseFloat(window.getComputedStyle(toc).top) || 0;
  return Math.ceil(stickyTop + toc.getBoundingClientRect().height + SCROLL_GAP);
};

const scrollToHash = (hash: string, container: HTMLElement | null) => {
  const target = document.getElementById(hash.replace("#", ""));

  if (!target) {
    return;
  }

  const headerOffset = getScrollOffset(container);
  const top =
    target.getBoundingClientRect().top + window.pageYOffset - headerOffset;

  window.scrollTo({ top, behavior: "smooth" });
};

export const LegalPageShell = ({ children, id }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const updateScrollOffset = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    container.style.setProperty(
      "--legal-scroll-offset",
      `${getScrollOffset(container)}px`,
    );
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const link = target.closest("a");
      const href = link?.getAttribute("href");

      if (!href?.startsWith("#")) {
        return;
      }

      event.preventDefault();
      updateScrollOffset();
      window.history.pushState(null, "", href);
      scrollToHash(href, containerRef.current);
    },
    [updateScrollOffset],
  );

  useEffect(() => {
    updateScrollOffset();
    window.addEventListener("resize", updateScrollOffset);

    if (window.location.hash) {
      window.setTimeout(() => {
        updateScrollOffset();
        scrollToHash(window.location.hash, containerRef.current);
      }, 0);
    }

    return () => window.removeEventListener("resize", updateScrollOffset);
  }, [updateScrollOffset]);

  return (
    <div
      className="ui pages legal-page"
      id={id}
      onClick={handleClick}
      ref={containerRef}
    >
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {children}
    </div>
  );
};
